import { bound } from "@frui.ts/helpers";
import { watchBusy } from "@frui.ts/screens";
import { action, computed, observable } from "mobx";
import ChargeManagementService, {
  DOWNLOAD_PAYMENT_CONFIRMATION_REQUEST_FAILURE,
  SEND_CHARGE_PAYMENT_CONFIRMATION_EMAIL_REQUEST_FAILURE,
} from "services/chargeManagementService";
import EventBus, {
  downloadPaymentConfirmationFailure,
  emailUpdated,
  putChargeContactSuccess,
  sendChargePaymentConfirmationEmailFailure,
  sendChargePaymentConfirmationEmailModalClosed,
  sendChargePaymentConfirmationEmailSuccess,
} from "services/eventBus";
import LocalizationService from "services/localizationService";
import SignalRNotificationService from "services/signalR/notification/signalRNotificationService";
import ChargeManagementEditPartViewModel from "./chargeManagementEditPartViewModel";
import React from "react";
import * as Sentry from "@sentry/react";
import { validate } from "@frui.ts/validation";
import { saveFileFromURL } from "../../../utils/fileUtils";
import NotificationPreferences from "models/notificationPreferences";
import ReceiptEmailModalViewModel from "./receiptEmailModalViewModel";
import EnumService from "services/enumService";
import ChargeTypesService from "services/chargeTypesService";
import TurnstileViewModel from "../../common/turnstileViewModel";
import { TurnstileInstance } from "@marsidev/react-turnstile";

export default class ChargeManagementReceiptViewModel extends ChargeManagementEditPartViewModel {
  EMAIL_REGEX = "%email%";
  turnstileViewModel: TurnstileViewModel;

  turnstileAction?: () => Promise<void>;
  recatpchaFailed?: () => void;
  @observable emailInputViewModel?: ReceiptEmailModalViewModel;
  @observable showModal = false;
  @observable emailModalOpened = false;
  @observable checkRequiredEmail = false;
  @observable turnstileError?: string;
  @observable turnstileInfo?: string;
  @observable private notificationPreferences = new NotificationPreferences();

  constructor(
    public localizationService: LocalizationService,
    protected chargeManagementService: ChargeManagementService,
    protected chargeTypesService: ChargeTypesService,
    protected eventBus: EventBus,
    protected enumService: EnumService,
    protected signalRNotificationService: SignalRNotificationService,
    private receiptEmailModalViewModelFactory: ReturnType<typeof ReceiptEmailModalViewModel.Factory>,
    turnstileViewModelFactory: ReturnType<typeof TurnstileViewModel.Factory>
  ) {
    super();
    this.navigationName = "receipt";
    this.turnstileViewModel = turnstileViewModelFactory("chargeManagementReceipt", true);
    this.unsubscriptions.push(
      this.eventBus.subscribe(downloadPaymentConfirmationFailure, this.downloadPaymentConfirmationFailure),
      this.eventBus.subscribe(sendChargePaymentConfirmationEmailModalClosed, this.sendChargePaymentConfirmationEmailModalClosed),
      this.eventBus.subscribe(sendChargePaymentConfirmationEmailFailure, this.sendChargePaymentConfirmationEmailFailure),
      this.eventBus.subscribe(sendChargePaymentConfirmationEmailSuccess, this.sendChargePaymentConfirmationEmailSuccess),
      this.eventBus.subscribe(putChargeContactSuccess, this.putChargeContactSuccess),
      this.eventBus.subscribe(emailUpdated, this.onEmailUpdated)
    );
  }

  protected onInitialize(): Promise<any> | void {
    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  get canEdit(): boolean {
    return true;
  }

  @computed
  get hasSecondStep() {
    return false;
  }

  @bound
  public validate(): boolean {
    return validate(this.notificationPreferences);
  }

  submit() {
    // silence is golden
  }

  @bound
  protected openChargePaymentConfirmationModal(token: string) {
    this.showModal = true;
    if (this.charge) {
      this.emailModalOpened = true;
      this.emailInputViewModel = this.receiptEmailModalViewModelFactory(this.charge, this.closeEmailModal, () =>
        this.sendPaymentConfirmationEmail(token)
      );
    }
  }

  @bound
  protected downloadPaymentConfirmationFailure(data: { type: string; payload: { type: string } }) {
    this.success = false;
    this.errorMessage = this.genericError;
  }

  @bound
  protected sendChargePaymentConfirmationEmailModalClosed() {
    this.emailModalOpened = false;
  }

  @bound
  protected sendChargePaymentConfirmationEmailSuccess() {
    this.success = true;
    this.emailModalOpened = false;
  }

  @bound
  protected sendChargePaymentConfirmationEmailFailure(data: ReturnType<typeof sendChargePaymentConfirmationEmailFailure>) {
    this.success = false;
    this.errorMessage = this.genericError;
  }

  @computed
  get customSuccessMessage() {
    return this.translate("receipt.success").replace(
      this.EMAIL_REGEX,
      this.charge && this.charge.emailAddress ? this.charge.emailAddress : "unknown"
    );
  }

  @computed
  get doesNotHaveEmail() {
    return !this.charge?.emailAddress;
  }

  @action.bound
  closeEmailModal() {
    this.showModal = false;
  }

  @bound
  protected onEmailUpdated(data: ReturnType<typeof emailUpdated>) {
    this.setEmailAddress(data.payload.emailAddress);
    if (this.emailInputViewModel) {
      this.emailInputViewModel = undefined;
    }
  }

  @bound
  protected async putChargeContactSuccess(data: ReturnType<typeof putChargeContactSuccess>) {
    if (data.payload.triggerer !== "contactChange") {
      this.emailInputViewModel = undefined;
      return;
    }

    this.clearAfterAsyncAction();

    if (!this.charge) {
      return;
    }

    this.charge.notificationPreferences.smsNotificationEnabled = data.payload.notificationPreferences?.smsNotificationEnabled;
    this.charge.notificationPreferences.emailNotificationEnabled = data.payload.notificationPreferences?.emailNotificationEnabled;
    this.charge.notificationPreferences._emailAddressNotification =
      data.payload.notificationPreferences?._emailAddressNotification;
    this.charge.notificationPreferences._emailConfirmation = data.payload.emailAddress;
    this.charge.emailAddress = data.payload.emailAddress;
    this.charge.notificationPreferences.phoneNumber = data.payload.notificationPreferences?.phoneNumber;
    if (data.payload.authCode) this.charge.authCode = data.payload.authCode;
  }

  @action.bound
  @watchBusy
  async sendPaymentConfirmationEmail(token: string) {
    // If the user doesn't have an email address, we need to display a modal window first.
    this.checkRequiredEmail = true;
    if (this.charge && !this.charge.emailAddress && !this.emailModalOpened && this.checkRequiredEmail) {
      this.openChargePaymentConfirmationModal(token);
      return;
    }

    // Once the user fills out their email (or has had it already, we submit it).
    this.success = false;
    this.errorMessage = undefined;

    if (!this.validate()) return;

    // Set action that will be called after turnstile has validated
    if (token) {
      try {
        await this.actionSendPaymentConfirmationEmail(token);
      } catch (error) {
        Sentry.withScope(scope => {
          scope.setTag("turnstile", "execute");
          const sentryError = error instanceof Error ? error : new Error(String(error));
          sentryError.name = "TurnstileExecuteError";
          Sentry.captureException(sentryError);
        });
        this.recatpchaFailed = () => {
          this.eventBus.publish(
            sendChargePaymentConfirmationEmailFailure({ type: SEND_CHARGE_PAYMENT_CONFIRMATION_EMAIL_REQUEST_FAILURE })
          );
        };
      }
    }
    this.turnstileViewModel.reset();
  }

  @action.bound
  @watchBusy
  async actionSendPaymentConfirmationEmail(token: string) {
    if (token) {
      this.isLoading = true;
      try {
        await this.chargeManagementService.sendChargePaymentConfirmationEmail(
          this.emailModalOpened ? this.charge?.emailAddress : undefined,
          token
        );
      } finally {
        this.isLoading = false;
      }
    }
  }

  @action.bound
  @watchBusy
  async downloadPaymentConfirmation(token: string) {
    this.success = false;
    this.errorMessage = undefined;
    this.checkRequiredEmail = false;
    try {
      // Set action that will be called after turnstile has validated
      if (token) {
        this.actionDownloadPaymentConfirmation(token);
      }
      this.turnstileViewModel.reset();
      this.recatpchaFailed = () => {
        this.eventBus.publish(downloadPaymentConfirmationFailure({ type: DOWNLOAD_PAYMENT_CONFIRMATION_REQUEST_FAILURE }));
      };
    } catch (error) {
      this.eventBus.publish(downloadPaymentConfirmationFailure({ type: DOWNLOAD_PAYMENT_CONFIRMATION_REQUEST_FAILURE }));
    }
  }

  @action.bound
  @watchBusy
  async actionDownloadPaymentConfirmation(token: string) {
    if (token) {
      await saveFileFromURL(() => this.chargeManagementService.downloadPaymentConfirmation(token!), "Charge management", {
        charge: this.charge,
      });
    }
  }
}
